"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  deleteAdminFeedAd,
  getAdminFeedAd,
  getFeedAdSettings,
  listAdminFeedAds,
  listFeedAds,
  setAdminFeedAdSettings,
  upsertAdminFeedAd,
} from "@/lib/feed/ads-api";
import type { UpsertFeedAdInput } from "@/lib/feed/ads";
import { queryKeys } from "@/lib/query-keys";
import { MissingSupabaseConfigError } from "@/lib/supabase/env";

function retryUnlessMissingConfig(failureCount: number, error: unknown) {
  return error instanceof MissingSupabaseConfigError ? false : failureCount < 1;
}

export function useFeedAds(
  enabled: boolean,
  placement: "home" | "community",
  communityId?: string | null,
) {
  return useQuery({
    queryKey:
      placement === "community" && communityId
        ? queryKeys.feedAdsCommunity(communityId)
        : queryKeys.feedAdsHome,
    queryFn: () => listFeedAds(placement, communityId),
    enabled:
      enabled && (placement === "home" || Boolean(communityId)),
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminFeedAds(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminFeedAds,
    queryFn: listAdminFeedAds,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useAdminFeedAd(adId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminFeedAd(adId ?? "new"),
    queryFn: () => getAdminFeedAd(adId!),
    enabled: enabled && Boolean(adId),
    retry: retryUnlessMissingConfig,
  });
}

export function useUpsertAdminFeedAd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpsertFeedAdInput) => upsertAdminFeedAd(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminFeedAds });
      await queryClient.invalidateQueries({ queryKey: ["admin-feed-ad"] });
      await queryClient.invalidateQueries({ queryKey: ["feed-ads"] });
    },
  });
}

export function useDeleteAdminFeedAd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (adId: string) => deleteAdminFeedAd(adId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminFeedAds });
      await queryClient.invalidateQueries({ queryKey: ["feed-ads"] });
    },
  });
}

export function useAdminFeedAdSettings(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adminFeedAdSettings,
    queryFn: getFeedAdSettings,
    enabled,
    retry: retryUnlessMissingConfig,
  });
}

export function useSetAdminFeedAdSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (slotEvery: number) => setAdminFeedAdSettings(slotEvery),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.adminFeedAdSettings,
      });
      await queryClient.invalidateQueries({ queryKey: ["feed-ads"] });
    },
  });
}
